<template>
  <div class="filters-container" :class="{ 'is-collapsed': !expanded }">
    <!-- CP-52: phones start collapsed so a deal card is within one viewport. Desktop keeps the heading and open panel. -->
    <button
      v-if="isPhone"
      type="button"
      class="filters-toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      aria-controls="advanced-filters-panel"
      @click="toggleExpanded"
    >
      <span class="filters-toggle-title">Advanced Filters</span>
      <span v-if="!expanded" class="filters-summary-chip">{{ summaryLabel }}</span>
      <i
        class="fas filters-toggle-icon"
        :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        aria-hidden="true"
      ></i>
    </button>
    <h3 v-else>Advanced Filters</h3>

    <div v-show="expanded" id="advanced-filters-panel">
    <div class="filters">
      <div class="filter-group">
        <label for="cruise-line">Cruise Line:</label>
        <select 
          id="cruise-line" 
          v-model="localFilters.cruise_line" 
          :disabled="isLoading"
          @change="onFilterChange"
        >
          <option value="">All Cruise Lines</option>
          <option v-for="line in availableCruiseLines" :key="line" :value="line">{{ line }}</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label for="destination">Destination:</label>
        <select 
          id="destination" 
          v-model="localFilters.destination" 
          :disabled="isLoading"
          @change="onFilterChange"
        >
          <option value="">All Destinations</option>
          <option v-for="dest in availableDestinations" :key="dest.id || dest" :value="dest.id || dest">{{ dest.label || dest }}</option>
        </select>
      </div>
      
      <div class="filter-group">
        <SearchableSelect
          id="departure-port"
          label="Departure Port:"
          v-model="localFilters.departure_port"
          :options="departurePortSelectOptions"
          :disabled="isLoading"
          empty-label="All Ports"
          placeholder="Search ports…"
          aria-label="Departure Port"
          @change="onFilterChange"
        />
      </div>
      
      <div class="filter-group">
        <label for="ship">Ship:</label>
        <select 
          id="ship" 
          v-model="localFilters.ship_name" 
          :disabled="isLoading"
          @change="onFilterChange"
        >
          <option value="">All Ships</option>
          <option v-for="ship in availableShips" :key="ship" :value="ship">{{ ship }}</option>
        </select>
      </div>
      
      <!-- CP-28 / PO ship-gate: Departure Date Range deferred until coverage >= 80% -->
      
      <div class="filter-group">
        <label>Price Range ($):</label>
        <div class="price-range">
          <input 
            type="number" 
            v-model.number="localFilters.min_price" 
            placeholder="Min Price" 
            :min="priceRange.min"
            :max="priceRange.max"
            class="price-input"
            :disabled="isLoading"
            @change="onFilterChange"
          />
          <span>to</span>
          <input 
            type="number" 
            v-model.number="localFilters.max_price" 
            placeholder="Max Price" 
            :min="priceRange.min"
            :max="priceRange.max"
            class="price-input"
            :disabled="isLoading"
            @change="onFilterChange"
          />
        </div>
      </div>
      
      <div class="filter-group">
        <label>Duration (nights):</label>
        <div class="duration-range">
          <input 
            type="number" 
            v-model.number="localFilters.min_duration" 
            placeholder="Min" 
            min="1"
            class="duration-input"
            :disabled="isLoading"
            @change="onFilterChange"
          />
          <span>to</span>
          <input 
            type="number" 
            v-model.number="localFilters.max_duration" 
            placeholder="Max" 
            min="1"
            class="duration-input"
            :disabled="isLoading"
            @change="onFilterChange"
          />
        </div>
      </div>
    </div>
    
    <div class="filter-actions">
      <button @click="applyFilters" class="filter-button" :disabled="isLoading">Apply Filters</button>
      <button @click="resetFilters" class="reset-button" :disabled="isLoading">Reset All</button>
    </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { normalizeCanonicalDestinations } from '@/utils/canonicalDestinations';
import {
  normalizeDeparturePortOptions,
  resolveDeparturePortSelection
} from '@/utils/canonicalDeparturePorts';
import SearchableSelect from '@/components/SearchableSelect.vue';

// Phones and large phones. Desktop (768+) keeps the panel open.
const MOBILE_FILTERS_QUERY = '(max-width: 767px)';

function viewportIsMobile() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(MOBILE_FILTERS_QUERY).matches;
}

export default {
  name: 'DealFilters',
  components: { SearchableSelect },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    // Explicit true/false overrides the viewport default. Omitted (null)
    // collapses on mobile and stays expanded on desktop (CP-52).
    // Not typed as Boolean: an absent Boolean prop would become false and
    // collapse the desktop panel too.
    filtersExpanded: {
      default: null,
      validator: (value) => value === null || typeof value === 'boolean'
    }
  },
  data() {
    return {
      expanded: true,
      isPhone: false,
      userToggled: false,
      mediaQuery: null,
      localFilters: {
        cruise_line: '',
        destination: '',
        departure_port: '',
        ship_name: '',
        start_date: '',
        end_date: '',
        min_duration: null,
        max_duration: null,
        min_price: null,
        max_price: null
      },
      availableCruiseLines: [],
      availableDestinations: [],
      availablePorts: [],
      availableShips: [],
      priceRange: { min: 0, max: 10000 },
      isLoading: false,
      refetchFilterOptionsTimer: null
    };
  },
  computed: {
    canonicalDeparturePorts() {
      return normalizeDeparturePortOptions(this.availablePorts);
    },
    departurePortSelectOptions() {
      return this.canonicalDeparturePorts.map((p) => ({ value: p.key, label: p.label }));
    },
    activeFilterCount() {
      const keys = [
        'cruise_line',
        'destination',
        'departure_port',
        'ship_name',
        'min_price',
        'max_price',
        'min_duration',
        'max_duration'
      ];
      return keys.reduce((count, key) => {
        const value = this.localFilters[key];
        if (value === '' || value === null || value === undefined) return count;
        return count + 1;
      }, 0);
    },
    summaryLabel() {
      const count = this.activeFilterCount;
      if (count === 0) return 'All deals';
      return count === 1 ? '1 filter' : `${count} filters`;
    }
  },
  watch: {
    filters: {
      handler(newFilters) {
        this.localFilters = { ...this.localFilters, ...newFilters };
      },
      deep: true,
      immediate: true
    },
    'localFilters.cruise_line'() { this.refetchFilterOptionsAndSanitize(); },
    'localFilters.destination'() { this.refetchFilterOptionsAndSanitize(); },
    'localFilters.departure_port'() { this.refetchFilterOptionsAndSanitize(); },
    'localFilters.ship_name'() { this.refetchFilterOptionsAndSanitize(); }
  },
  created() {
    this.applyViewport(viewportIsMobile());
  },
  mounted() {
    this.loadFilterOptions();
    this.localFilters = { ...this.localFilters, ...this.filters };
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mediaQuery = window.matchMedia(MOBILE_FILTERS_QUERY);
      this.onMediaQueryChange = (event) => {
        this.applyViewport(event.matches);
      };
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onMediaQueryChange);
      }
    }
  },
  beforeUnmount() {
    if (this.refetchFilterOptionsTimer) {
      clearTimeout(this.refetchFilterOptionsTimer);
    }
    if (!this.mediaQuery) return;
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.onMediaQueryChange);
    } else if (this.mediaQuery.removeListener) {
      this.mediaQuery.removeListener(this.onMediaQueryChange);
    }
  },
  methods: {
    applyViewport(matchesMobile) {
      this.isPhone = matchesMobile;
      if (typeof this.filtersExpanded === 'boolean') {
        this.expanded = this.filtersExpanded;
        return;
      }
      if (!matchesMobile) {
        this.expanded = true;
        return;
      }
      if (!this.userToggled) {
        this.expanded = false;
      }
    },
    toggleExpanded() {
      this.userToggled = true;
      this.expanded = !this.expanded;
    },
    filterParams() {
      const p = {};
      if (this.localFilters.cruise_line) p.cruise_line = this.localFilters.cruise_line;
      if (this.localFilters.destination) p.destination = this.localFilters.destination;
      if (this.localFilters.departure_port) p.departure_port = this.localFilters.departure_port;
      if (this.localFilters.ship_name) p.ship_name = this.localFilters.ship_name;
      return p;
    },
    refetchFilterOptionsAndSanitize() {
      if (this.refetchFilterOptionsTimer) clearTimeout(this.refetchFilterOptionsTimer);
      this.refetchFilterOptionsTimer = setTimeout(() => {
        this.refetchFilterOptionsTimer = null;
        this.loadFilterOptions(this.filterParams()).then(() => {
          const lines = this.availableCruiseLines || [];
          const dest = this.availableDestinations || [];
          const ports = this.availablePorts || [];
          const ships = this.availableShips || [];
          if (this.localFilters.cruise_line && !lines.includes(this.localFilters.cruise_line)) this.localFilters.cruise_line = '';
          const destIds = dest.map(d => (d && d.id) ? d.id : d);
          if (this.localFilters.destination && !destIds.includes(this.localFilters.destination)) this.localFilters.destination = '';
          const portOptions = normalizeDeparturePortOptions(ports);
          if (this.localFilters.departure_port) {
            const resolved = resolveDeparturePortSelection(portOptions, this.localFilters.departure_port);
            if (!resolved) this.localFilters.departure_port = '';
            else if (resolved !== this.localFilters.departure_port) this.localFilters.departure_port = resolved;
          }
          if (this.localFilters.ship_name && !ships.includes(this.localFilters.ship_name)) this.localFilters.ship_name = '';
        });
      }, 50);
    },
    async loadFilterOptions(params = {}) {
      this.isLoading = true;
      try {
        const response = await api.getFilterOptions(params);
        this.availableCruiseLines = response.data.cruise_lines || [];
        this.availableDestinations = normalizeCanonicalDestinations(response.data.destinations);
        this.availablePorts = response.data.departure_ports || [];
        if (this.localFilters.departure_port) {
          const resolved = resolveDeparturePortSelection(
            normalizeDeparturePortOptions(this.availablePorts),
            this.localFilters.departure_port
          );
          if (resolved && resolved !== this.localFilters.departure_port) {
            this.localFilters.departure_port = resolved;
          }
        }
        this.availableShips = response.data.ships || [];
        // CP-127: Hot Deals recovery labels ("Caribbean", "From Miami, FL").
        this.$emit('options-loaded', {
          destinations: this.availableDestinations,
          departurePorts: this.canonicalDeparturePorts
        });
        if (response.data.price_range) {
          this.priceRange = {
            min: response.data.price_range.min || 0,
            max: response.data.price_range.max || 10000
          };
        }
        return Promise.resolve();
      } catch (error) {
        console.error('Error loading filter options:', error);
        return Promise.resolve();
      } finally {
        this.isLoading = false;
      }
    },
    onFilterChange() {
      // Emit filter changes immediately (auto-apply)
      this.applyFilters();
    },
    applyFilters() {
      // Create a clean filter object with only non-empty values
      const filtersToEmit = { ...this.localFilters };
      
      // Remove empty values
      Object.keys(filtersToEmit).forEach(key => {
        const value = filtersToEmit[key];
        if (value === '' || value === null || value === undefined) {
          delete filtersToEmit[key];
        }
      });
      
      this.$emit('filter-change', filtersToEmit);
    },
    resetFilters() {
      this.localFilters = {
        cruise_line: '',
        destination: '',
        departure_port: '',
        ship_name: '',
        start_date: '',
        end_date: '',
        min_duration: null,
        max_duration: null,
        min_price: null,
        max_price: null
      };
      this.applyFilters();
    },
    /**
     * CP-127: empty-results recovery drops one filter (e.g.
     * { max_price: null }) and re-applies, same auto-apply path as editing
     * the field. No reload.
     */
    clearFilter(patch) {
      const p = patch && typeof patch === 'object' ? patch : {};
      const next = { ...this.localFilters };
      Object.keys(p).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(next, key)) next[key] = p[key];
      });
      this.localFilters = next;
      this.applyFilters();
    }
  }
};
</script>

<style scoped>
.filters-container {
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  padding: var(--space-5);
  margin-bottom: var(--space-5);
  box-shadow: var(--shadow-card);
}

h3 {
  margin-top: 0;
  margin-bottom: var(--space-5);
  color: var(--color-text);
  font-family: var(--font-heading);
}

.filters-toggle {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: var(--touch-min);
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}

.filters-container:not(.is-collapsed) .filters-toggle {
  margin-bottom: var(--space-5);
}

.filters-toggle-title {
  font-family: var(--font-heading);
  font-size: var(--text-h3);
  font-weight: 700;
  line-height: var(--line-height-tight);
}

.filters-summary-chip {
  margin-left: auto;
  font-family: var(--font-body);
  font-size: var(--text-caption);
  font-weight: 600;
  line-height: 1.2;
  color: var(--cs-signal-teal);
  background: var(--cs-signal-mist);
  border-radius: var(--radius-pill);
  padding: 4px 10px;
  white-space: nowrap;
}

.filters-toggle-icon {
  color: var(--cs-muted);
  font-size: var(--text-small);
}

.filters-container.is-collapsed .filters-toggle-icon {
  margin-left: var(--space-2);
}

.filters-container:not(.is-collapsed) .filters-toggle-icon {
  margin-left: auto;
}

.filters-toggle:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.filters-container.is-collapsed {
  padding: var(--space-2) var(--space-4);
  margin-bottom: var(--space-4);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
}

.filter-group {
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  display: block;
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
}

.filter-group select,
.filter-group input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--text-body);
  background: var(--color-surface);
  color: var(--color-text);
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.filter-group select:disabled,
.filter-group input:disabled {
  background-color: var(--color-border-light);
  cursor: not-allowed;
  opacity: 0.8;
}

.date-range, .price-range, .duration-range {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  overflow: hidden;
}

.date-input, .price-input, .duration-input {
  flex: 1;
  min-width: 0;
  width: auto;
}

.date-range span,
.price-range span,
.duration-range span {
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

.filter-button, .reset-button {
  padding: var(--space-3) var(--space-5);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-normal);
}

.filter-button {
  background-color: var(--cs-fill);
  color: white;
}

.filter-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.filter-button:focus-visible,
.reset-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.filter-button:disabled {
  background-color: var(--color-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.reset-button {
  background-color: transparent;
  color: var(--cs-signal-teal);
  border: 1.5px solid var(--cs-signal-teal);
}

.reset-button:hover:not(:disabled) {
  background-color: var(--cs-signal-mist);
}

.reset-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.filter-button {
  background-color: var(--cs-fill);
}

.filters-container {
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-lg);
}

@media (max-width: 640px) {
  .filters {
    flex-direction: column;
  }
  .filter-group {
    min-width: 0;
    width: 100%;
  }
  .filter-actions {
    flex-direction: column;
  }
  .filter-button, .reset-button {
    width: 100%;
    min-height: var(--touch-min);
  }
}
</style>
